import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, set } from '@ember/object';
import { task, timeout } from 'ember-concurrency';
import { delRelationship, addRelationship } from 'beebee-base/lib/utility';
import { createPopper } from '@popperjs/core';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{#if this.showAddNew}}\n  <div class=\'grid-x\'>\n    <div class=\'cell medium-12\'>\n      <SelectAssociation\n        @applicationController={{@applicationController}}\n        @relatedModelType={{@relatedModelType}}\n        @defaultStatusName={{@defaultStatusName}}\n        @action={{perform this.addRelatedTask}}\n        @addNew={{this.addNew}}\n        @wrapLabelSearchInput={{@wrapLabelSearchInput}}\n        @documentType={{@documentType}}\n        @status={{this.defStatus}}\n        @appendQuery={{@appendQuery}}\n        @filter_by_contact={{@filter_by_contact}}\n        @filter_by_product={{@filter_by_product}}\n        @filter_by_project={{@filter_by_project}}\n      />\n    </div>\n  </div>\n{{/if}}\n\n{{! @relatedHasMany.length: the hasMany ManyArray is a STABLE reference, so\n    did-update never fires when records are pushed into it (e.g. a payment\n    saved from bb-payment-form-modal) — only on @total, which needs a server\n    round-trip. Tracking the length refreshes the chip map immediately. }}\n<div\n  class=\'{{this.chipWrapClass}}\'\n  {{did-update this.reloadMap @total @relatedHasMany @relatedHasMany.length}}\n>\n  <div class=\'cell medium-12\'>\n    {{#each this.relatedHasManyMap as |hasManyItem|}}\n      {{#if hasManyItem.id}}\n        <BbChip\n          @applicationController={{@applicationController}}\n          @model={{@relatedModelType}}\n          @item={{hasManyItem}}\n          @openDropFn={{this.openDropTask}}\n        />\n      {{/if}}\n    {{/each}}\n  </div>\n</div>\n\n<BbHasManyPaginator\n  @relatedModelType={{@relatedModelType}}\n  @relatedHasMany={{@relatedHasMany}}\n  @item={{@item}}\n  @status={{@status}}\n  @total={{@total}}\n/>");

var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
let HasManyComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  restartable: true
}), _dec3 = task({
  drop: true
}), _dec4 = task({
  drop: true
}), _class = class HasManyComponent extends Component {
  get isEditable() {
    return this.args.isEditable === false ? false : true;
  }
  get saveRelation() {
    return this.args.saveRelation === true ? true : false;
  }
  get addNew() {
    return this.args.addNew === false ? false : true;
  }
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "session", _descriptor3, this);
    _initializerDefineProperty(this, "notify", _descriptor4, this);
    _initializerDefineProperty(this, "relatedHasManyMap", _descriptor5, this);
    this.loadRelatedHasManyMap.perform();
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this.loadRelatedHasManyMap?.cancelAll?.();
    this.removeRelatedTask?.cancelAll?.();
    this.addRelatedTask?.cancelAll?.();
    if (this.args.applicationController?.prevChip?._this === this) {
      this.args.applicationController.zfprevChip?.close();
    }
    this._popperInstance?.destroy();
    this._popperInstance = null;
  }
  get defStatus() {
    const status = this.args.status;
    const optionField = this.session.option_field;
    const defStatusName = this.args.defaultStatusName;
    const relatedModelType = this.args.relatedModelType;
    if (status) return status;
    if (optionField && defStatusName && optionField.def_status?.[defStatusName]) {
      const defStatusId = optionField.def_status[defStatusName].toString();
      const defStatuses = this.session.me?.organization?.[`${relatedModelType}_states`] || [];
      return defStatuses.find(s => s.id === defStatusId) || null;
    }
    return null;
  }
  get showAddNew() {
    const isEditable = this.isEditable;
    const maxItems = this.args.maxItems;
    const length = this.relatedHasManyMap?.length ?? 0;
    return isEditable && (maxItems == null || length < maxItems);
  }
  get chipWrapClass() {
    return this.args.chipWrapClass ?? 'grid-x has-many-grid';
  }
  *loadRelatedHasManyMap() {
    const arrFilter = yield this.args.relatedHasMany;
    if (!Array.isArray(arrFilter)) {
      this.relatedHasManyMap = [];
      return;
    }
    const statusId = this.defStatus?.id;
    const relatedModelType = this.args.relatedModelType;
    let filteredArray = arrFilter;
    if (statusId) {
      try {
        const filtered = yield Promise.all(arrFilter.map(async item => {
          const relatedState = await item.get(`${relatedModelType}_state`);
          return relatedState?.id === statusId ? item : null;
        }));
        filteredArray = filtered.filter(Boolean);
      } catch {
        filteredArray = arrFilter;
      }
    }
    const unique = Array.from(new Map(filteredArray.map(item => [item.id, item])).values());
    this.relatedHasManyMap = unique;
  }
  *openDropTask(item, chipId) {
    const refEl = document.querySelector(`#${chipId}`);
    const popEl = document.querySelector('#zfprevChip');

    // The preview popper is driven entirely through applicationController.prevChip;
    // without it, set() below would assert on undefined.
    if (!popEl || !refEl || !this.args.applicationController) return;
    const prevChip = this.args.applicationController.prevChip;
    if (popEl.classList.contains('is-open') && prevChip?.relDivId === chipId) {
      this.args.applicationController.zfprevChip?.close();
      return;
    }

    // [regression-fix] model and id were present in Classic (via chip-item standalone path)
    // but dropped when openDropFn was introduced; setPreviewTask guards on prevChip.id
    // and uses prevChip.model for store.findRecord — without them preview/navigation silently no-ops
    set(this.args.applicationController, 'prevChip', {
      chipLabel: item.label,
      chipSubLabel: item.sub_label,
      chipStatus: item.status_name,
      isEditable: this.isEditable,
      removeRelated: this.removeRelated,
      item,
      model: this.args.relatedModelType,
      id: item.id,
      _this: this,
      relDivId: chipId
    });
    yield timeout(50);
    if (this.isDestroying || this.isDestroyed) return;
    popEl.classList.add('is-open');
    document.body.classList.add('dropdown-open');
    this._popperInstance?.destroy();
    this._popperInstance = createPopper(refEl, popEl, {
      placement: 'bottom',
      modifiers: [{
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'right', 'top', 'left']
        }
      }]
    });
  }
  reloadMap() {
    this.loadRelatedHasManyMap.perform();
  }
  removeRelated(item) {
    this.removeRelatedTask.perform(item);
  }
  *removeRelatedTask(item) {
    const relatedHasMany = yield this.args.relatedHasMany;
    if (Array.isArray(relatedHasMany)) {
      const _i = relatedHasMany.indexOf(item);
      if (_i !== -1) {
        relatedHasMany.splice(_i, 1);
      }
    }
    if (typeof this.args.action === 'function') {
      this.args.action(null);
    }
    if (typeof this.args.actionRemove === 'function') {
      this.args.actionRemove(item.id);
    }
    if (this.saveRelation && this.args.item?.id) {
      this._saveAssociation(item.id, this.args.item.id, 'del');
    } else {
      delRelationship(this.args.item, item);
    }
    this.args.applicationController?.zfprevChip?.close();
    this.loadRelatedHasManyMap.perform();
  }
  *addRelatedTask(id) {
    const relatedHasMany = yield this.args.relatedHasMany;
    if (this._checkIsPresent(relatedHasMany, id)) {
      this.notify.warning(this.intl.t('already exist'));
      return false;
    }
    const itemFromStore = this.store.peekRecord(this.args.relatedModelType, id);
    if (!itemFromStore) return false;
    if (typeof this.args.action === 'function') {
      this.args.action(id);
    }
    if (Array.isArray(relatedHasMany)) {
      relatedHasMany.push(itemFromStore);
    }
    if (this.saveRelation && this.args.item?.id) {
      this._saveAssociation(id, this.args.item.id, 'add');
    } else {
      addRelationship(this.args.item, itemFromStore);
    }
    this.loadRelatedHasManyMap.perform();
  }
  _saveAssociation(modelId, relatedModelId, operation) {
    this.args.applicationController.saveRelation(this.args.relatedModelType, modelId, this.args.item?.constructor?.modelName, relatedModelId, operation);
  }
  _checkIsPresent(relatedHasMany, id) {
    return Array.isArray(relatedHasMany) && relatedHasMany.find(i => i.id === id);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "relatedHasManyMap", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class.prototype, "loadRelatedHasManyMap", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "loadRelatedHasManyMap"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "openDropTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "openDropTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reloadMap", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reloadMap"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeRelated", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeRelated"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeRelatedTask", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "removeRelatedTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addRelatedTask", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "addRelatedTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, HasManyComponent);

export { HasManyComponent as default };
//# sourceMappingURL=has-many.js.map
